import { useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { apiRequest } from "@/lib/queryClient";
import { invalidateCalendar } from "@/lib/calendar-cache";
import type {
  CalendarAttendee,
  CalendarEventInput,
  CalendarItem,
  CalendarItemKind,
  CalendarPulseLink,
  CalendarSourceType,
} from "@shared/calendar-contract";
import { CALENDAR_TIMEZONE } from "@shared/calendar-contract";

export type {
  CalendarAttendee,
  CalendarEventInput,
  CalendarItem,
  CalendarItemKind,
  CalendarPulseLink,
  CalendarSourceType,
} from "@shared/calendar-contract";
export {
  CALENDAR_QUERY_ROOTS,
  invalidateCalendar,
} from "@/lib/calendar-cache";

export interface CalendarOptions {
  users: Array<{ id: number; name: string; shortCode?: string | null; role?: string }>;
  projects: Array<{ id: string; name: string; thumbnail: string | null }>;
  isAdmin: boolean;
}

export interface CalendarCallAction {
  id: string;
  requesterUserId?: number;
  requesterName?: string | null;
  recipientUserId?: number;
  recipientName?: string | null;
  projectId: string | null;
  projectName: string | null;
  projectThumbnail?: string | null;
  callType: "audio" | "video";
  topic?: string | null;
  createdAt: string;
  status: string;
  canStart?: boolean;
  canDismiss?: boolean;
  canCancel?: boolean;
}

export interface CalendarSubscription {
  id: string;
  scope: "personal" | "company";
  createdAt: string;
  lastUsedAt?: string | null;
}

/**
 * A local clock for calendar presentation only.  Calendar data remains
 * push/focus/reconnect driven; ticking this clock must never cause an agenda
 * request.  Seconds are used rather than a minute interval so a call crosses
 * an exact one/four-hour boundary without waiting for the following minute.
 */
export function useCalendarClock() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const tick = () => {
      setNow(Date.now());
      // Stay aligned to wall-clock seconds. The displayed value is still
      // minute-rounded by formatCalendarCallCountdown below.
      timeout = setTimeout(tick, Math.max(1, 1_000 - (Date.now() % 1_000)));
    };
    timeout = setTimeout(tick, Math.max(1, 1_000 - (Date.now() % 1_000)));
    return () => clearTimeout(timeout);
  }, []);

  return now;
}

const TERMINAL_CALENDAR_CALL_STATUSES = new Set([
  "cancelled",
  "canceled",
  "declined",
  "started",
  "completed",
  "ended",
]);

/**
 * Scheduled calls are calendar events, not callback requests or ordinary
 * events. A declined attendee must not receive a call reminder, and terminal
 * call entries must never return to the Scheduled UI simply because its local
 * clock changes.
 */
export function isPriorityCalendarCall(item: CalendarItem, now = Date.now()) {
  return isUpcomingCalendarCall(item, now)
    && Date.parse(item.startAt) <= now + 48 * 60 * 60_000;
}

export function isUpcomingCalendarCall(item: CalendarItem, now = Date.now()) {
  if (item.kind !== "call" || item.sourceType === "call_history") return false;
  if (item.response === "declined") return false;
  if (TERMINAL_CALENDAR_CALL_STATUSES.has((item.status ?? "").toLowerCase())) return false;
  const start = Date.parse(item.startAt);
  // Keep an unstarted call accessible at its scheduled time instead of making
  // its join/edit entry disappear as soon as the countdown reaches zero.
  return Number.isFinite(start) && (start >= now || Date.parse(item.endAt) > now);
}

/**
 * Countdown values are rounded up so "1 minute 1 second" is honestly shown
 * as two minutes.  Calls at or just beyond their start show Due, never a
 * negative duration.
 */
/**
 * Countdown label for a scheduled call:
 * - within 24 h: remaining time ("1h 05m", "Due")
 * - more than 24 h but at most 6 days away: weekday + start time ("Thu 14:30")
 * - more than 6 days away: calendar date + start time ("24 Sep 14:30")
 * Weekday/date are rendered in the fixed calendar timezone (Europe/Berlin).
 */
export function formatCalendarCallCountdown(startAt: string, now = Date.now()) {
  const start = Date.parse(startAt);
  const minutes = Number.isFinite(start)
    ? Math.max(0, Math.ceil((start - now) / 60_000))
    : 0;
  if (minutes === 0) return "Due";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
  const time = formatInTimeZone(start, CALENDAR_TIMEZONE, "HH:mm");
  if (hours < 6 * 24) return `${formatInTimeZone(start, CALENDAR_TIMEZONE, "EEE")} ${time}`;
  return `${formatInTimeZone(start, CALENDAR_TIMEZONE, "d MMM")} ${time}`;
}

/** True when the countdown label is an absolute weekday/date rather than remaining time. */
export function isCalendarCountdownAbsolute(startAt: string, now = Date.now()) {
  const start = Date.parse(startAt);
  return Number.isFinite(start) && start - now >= 24 * 3_600_000;
}

export type ScheduledView = number | "all" | null;

function rangeUrl(from: string, to: string, viewAsUserId?: ScheduledView) {
  const params = new URLSearchParams({ from, to });
  if (viewAsUserId === "all") params.set("view", "all");
  else if (viewAsUserId != null) params.set("viewAsUserId", String(viewAsUserId));
  return `/api/calendar/agenda?${params.toString()}`;
}

/**
 * Calendar cache synchronization is push/focus/reconnect based. There is
 * deliberately no additional full-agenda polling loop.
 */
export function useCalendarRefreshEvents() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const refresh = () => void invalidateCalendar(queryClient);
    window.addEventListener("calendar:refresh", refresh);
    return () => {
      window.removeEventListener("calendar:refresh", refresh);
    };
  }, [queryClient]);
}

/**
 * `viewAsUserId` lets an admin read another team member's agenda and `all`
 * requests the set-union team agenda. Live `calendar.invalidate` fan-out
 * targets the viewed user, not the admin, so borrowed views poll while active.
 */
export function useCalendarAgenda(from: string, to: string, viewAsUserId: ScheduledView = null) {
  return useQuery<{ items: CalendarItem[]; truncated: boolean }>({
    queryKey: viewAsUserId == null
      ? ["/api/calendar/agenda", { from, to }]
      : viewAsUserId === "all"
        ? ["/api/calendar/agenda", { from, to, view: "all" }]
        : ["/api/calendar/agenda", { from, to, viewAsUserId }],
    queryFn: async () => {
      const response = await apiRequest("GET", rangeUrl(from, to, viewAsUserId));
      return response.json();
    },
    refetchInterval: viewAsUserId == null ? false : 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useCalendarOptions(enabled = true) {
  return useQuery<CalendarOptions>({
    queryKey: ["/api/calendar/options"],
    enabled,
    staleTime: 5 * 60_000,
    refetchOnReconnect: true,
  });
}

export function useCalendarEvent(id: string | null, enabled = true) {
  return useQuery<CalendarItem>({
    queryKey: ["/api/calendar/events", id],
    enabled: enabled && !!id,
    staleTime: 0,
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/calendar/events/${encodeURIComponent(id!)}`);
      const body = await response.json();
      return body.item ?? body;
    },
  });
}

export function useCalendarCallActions() {
  return useQuery<{ items: CalendarCallAction[] }>({
    queryKey: ["/api/calendar/call-actions"],
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useCreateCalendarEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CalendarEventInput) => {
      const response = await apiRequest("POST", "/api/calendar/events", input);
      return response.json();
    },
    onSuccess: () => invalidateCalendar(queryClient),
  });
}

export function useUpdateCalendarEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, version, input }: { id: string; version: number; input: CalendarEventInput }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/calendar/events/${encodeURIComponent(id)}`,
        { ...input, version },
      );
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.removeQueries({ queryKey: ["/api/calendar/events", variables.id] });
      return invalidateCalendar(queryClient);
    },
  });
}

export function useCancelCalendarEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, version }: { id: string; version: number }) => {
      const response = await apiRequest(
        "DELETE",
        `/api/calendar/events/${encodeURIComponent(id)}`,
        { version },
      );
      return response.status === 204 ? null : response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.removeQueries({ queryKey: ["/api/calendar/events", variables.id] });
      return invalidateCalendar(queryClient);
    },
  });
}

export function useRespondCalendarEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, response }: { id: string; response: "accepted" | "declined" }) => {
      const result = await apiRequest(
        "POST",
        `/api/calendar/events/${encodeURIComponent(id)}/respond`,
        { response },
      );
      return result.json();
    },
    onSuccess: () => invalidateCalendar(queryClient),
  });
}

export function useCreateCallbackRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      recipientUserIds: number[];
      projectId: string | null;
      callType: "audio" | "video";
      topic?: string | null;
    }) => {
      const response = await apiRequest("POST", "/api/calendar/call-actions", input);
      return response.json();
    },
    onSuccess: () => invalidateCalendar(queryClient),
  });
}

export function usePatchCallAction(action: "dismiss" | "cancel") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "POST",
        `/api/calendar/call-actions/${encodeURIComponent(id)}/${action}`,
      );
      return response.status === 204 ? null : response.json();
    },
    onSuccess: () => invalidateCalendar(queryClient),
  });
}

export function useUpdateCallActionTopic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, topic, expectedTopic }: {
      id: string;
      topic: string | null;
      expectedTopic: string | null;
    }) => {
      const trimmedTopic = topic?.trim() || null;
      if (trimmedTopic && trimmedTopic.length > 200) {
        throw new Error("Topic must be 200 characters or fewer.");
      }
      await apiRequest(
        "PATCH",
        `/api/calendar/call-actions/${encodeURIComponent(id)}/topic`,
        { topic: trimmedTopic, expectedTopic },
      );
    },
    onSuccess: () => invalidateCalendar(queryClient),
  });
}

export function useCalendarHistory(enabled: boolean) {
  return useInfiniteQuery<{ items: CalendarItem[]; nextCursor: string | null }>({
    queryKey: ["/api/calendar/history"],
    enabled,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam) params.set("cursor", String(pageParam));
      const response = await apiRequest(
        "GET",
        `/api/calendar/history${params.size ? `?${params.toString()}` : ""}`,
      );
      return response.json();
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
    refetchOnReconnect: true,
  });
}

export function useCalendarSubscriptions(enabled: boolean) {
  return useQuery<{ items: CalendarSubscription[] }>({
    queryKey: ["/api/calendar/subscriptions"],
    enabled,
  });
}

export function useCreateCalendarSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scope: "personal" | "company") => {
      const response = await apiRequest("POST", "/api/calendar/subscriptions", { scope });
      return response.json() as Promise<{ id: string; url: string; scope: "personal" | "company" }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/calendar/subscriptions"] }),
  });
}

export function useRevokeCalendarSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/calendar/subscriptions/${encodeURIComponent(id)}`,
      );
      return response.status === 204 ? null : response.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/calendar/subscriptions"] }),
  });
}